import { useAuth } from "@babackapp/auth-client/hooks";
import { AuthError } from "@babackapp/auth-client/types";
import type { UserDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { type FormEvent, useState } from "react";

interface MfaChallengeFormProps {
  mfaToken: string;
  onVerified: (user: UserDto) => void;
  /** The challenge has expired: the password has to be typed again. */
  onRestart: () => void;
}

export default function MfaChallengeForm({
  mfaToken,
  onVerified,
  onRestart,
}: MfaChallengeFormProps) {
  const t = useTranslation();
  const auth = useAuth();
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [value, setValue] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const user = await auth.verifyMfa(
        isRecoveryMode
          ? { mfaToken, recoveryCode: value }
          : { mfaToken, code: value },
      );
      onVerified(user);
    } catch (error) {
      setValue("");
      if (
        error instanceof AuthError &&
        error.code === "AUTH_MFA_INVALID_CHALLENGE"
      ) {
        onRestart();
      }
    }
  };

  const switchMode = () => {
    setIsRecoveryMode((previous) => !previous);
    setValue("");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-medium text-gray-900">
          {t("signin.mfa.title")}
        </h3>
        <p className="text-sm text-gray-600">
          {isRecoveryMode
            ? t("signin.mfa.recoveryDescription")
            : t("signin.mfa.codeDescription")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfa-code">
          {isRecoveryMode
            ? t("signin.mfa.recoveryLabel")
            : t("signin.mfa.codeLabel")}
        </Label>
        {isRecoveryMode ? (
          <Input
            id="mfa-code"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="off"
            placeholder="xxxxx-xxxxx"
            autoFocus
          />
        ) : (
          <Input
            id="mfa-code"
            value={value}
            onChange={(event) =>
              setValue(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
          />
        )}
      </div>

      <Button
        className="w-full"
        type="submit"
        disabled={auth.isLoading || (!isRecoveryMode && value.length !== 6)}
      >
        {t("signin.mfa.verify")}
      </Button>

      <div className="flex flex-col items-center gap-2 text-sm">
        <button
          type="button"
          onClick={switchMode}
          className="font-medium text-purple-600 hover:text-purple-500"
        >
          {isRecoveryMode
            ? t("signin.mfa.useAuthenticator")
            : t("signin.mfa.useRecoveryCode")}
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="text-gray-600 hover:text-gray-900"
        >
          {t("signin.mfa.back")}
        </button>
      </div>
    </form>
  );
}
